<script setup lang="ts">
/**
 * The Backlog lane of the `/executions` board, grouped as a work list is: a parent (an epic, a campaign) is one row
 * that folds its children, in the list's order (priority, then position, then age), and the items that sit under no
 * parent follow it. A parent's row counts where all of its children stand on the board (`ExecutionBoard.rollups`, the
 * server's count over every lane), not only the ones still in the Backlog, so an epic whose work has started says how
 * far it has gone. Drawn the same in the lane and in the lane opened to the page's width (`wide`), where the cards lay
 * out in columns. The board sends one page of rows; the page pages them.
 */
import { BOARD_LANES, type BoardLane, type WorkItemCard as Card, type ExecutionBoard } from '@zerohuman/shared'
import { ZhGroupRow, ZhStatusPill } from '@zerohuman/ui'
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import WorkItemCard from '@/components/WorkItemCard.vue'
import { LANE_LABELS, LANE_TONES, PRIORITY_LABELS } from '@/lib/boardLanes'

const props = defineProps<{
    board: ExecutionBoard
    now: number
    /** The lane fills the page: cards lay out in columns. */
    wide?: boolean
    /** The parents shown open, by item id: kept by the page, so a parent stays open across reads and widths. */
    opened: ReadonlySet<string>
}>()
const emit = defineEmits<{ changed: []; 'update:open': [id: string, open: boolean] }>()

/** The order a parent's rollup names the lanes in: the live work first. */
const ROLLUP_LANES: readonly BoardLane[] = ['inProgress', 'blocked', 'ready', 'backlog', 'complete']

/**
 * The Backlog's parents, each with its children still in the Backlog, and the items under no parent in it. A child
 * whose parent is not in the Backlog (the search left it out, or a run holds it) stands with the items under none.
 */
const layout = computed(() => {
    const backlog = props.board.items.backlog
    const inBacklog = new Set(backlog.map(card => card.id))
    const children = new Map<string, Card[]>()
    for (const card of backlog) {
        if (card.parentId && inBacklog.has(card.parentId)) {
            children.set(card.parentId, [...(children.get(card.parentId) ?? []), card])
        }
    }
    const groups: { parent: Card; children: Card[] }[] = []
    const loose: Card[] = []
    for (const card of backlog) {
        if (card.parentId && inBacklog.has(card.parentId)) continue
        if (props.board.rollups[card.id]) groups.push({ parent: card, children: children.get(card.id) ?? [] })
        else loose.push(card)
    }
    return { groups, loose }
})

function total(parent: Card): number {
    const lanes = props.board.rollups[parent.id]
    return lanes ? BOARD_LANES.reduce((sum, lane) => sum + lanes[lane], 0) : 0
}
function rollup(parent: Card): [BoardLane, number][] {
    const lanes = props.board.rollups[parent.id]
    return ROLLUP_LANES.flatMap(lane => (lanes && lanes[lane] > 0 ? [[lane, lanes[lane]] as [BoardLane, number]] : []))
}
const cardsClass = computed(() =>
    props.wide ? 'grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4' : 'flex flex-col gap-3',
)
</script>

<template>
    <div class="flex flex-col gap-3">
        <div v-for="group in layout.groups" :key="group.parent.id" :data-backlog-parent="group.parent.id" class="border-border rounded-md border p-3 text-sm">
            <ZhGroupRow :count="total(group.parent)" :open="opened.has(group.parent.id)" @update:open="open => emit('update:open', group.parent.id, open)">
                <template #title><span class="font-medium">{{ group.parent.title }}</span></template>
                <template #rollup>
                    <span v-for="[lane, count] in rollup(group.parent)" :key="lane" :data-rollup="lane">
                        <ZhStatusPill :tone="LANE_TONES[lane]" :pulse="false">{{ count }} {{ LANE_LABELS[lane].toLowerCase() }}</ZhStatusPill>
                    </span>
                </template>
                <template #aside>
                    <span v-if="group.parent.priority" class="text-muted-foreground text-xs">{{ PRIORITY_LABELS[group.parent.priority] }}</span>
                    <RouterLink :to="`/work-items/${group.parent.id}`" :aria-label="`Open ${group.parent.title}`" class="text-muted-foreground hover:text-primary text-xs underline underline-offset-2">Open</RouterLink>
                </template>
                <div v-if="group.children.length > 0" :class="cardsClass">
                    <WorkItemCard v-for="item in group.children" :key="item.id" :item="item" :now="now" @changed="emit('changed')" />
                </div>
                <p v-else class="text-muted-foreground text-xs">None of its items are in the Backlog.</p>
            </ZhGroupRow>
        </div>
        <h3 v-if="layout.groups.length > 0 && layout.loose.length > 0" class="text-muted-foreground text-xs font-medium">Not under a parent</h3>
        <div v-if="layout.loose.length > 0" data-backlog-loose :class="cardsClass">
            <WorkItemCard v-for="item in layout.loose" :key="item.id" :item="item" :now="now" @changed="emit('changed')" />
        </div>
    </div>
</template>
